// Iter49 — Chain Map d3 directed graph view.
// Two layouts: 'force' (d3.forceSimulation) and 'layered' (Layer 1 top → Layer 5 bottom).
// Click node → onSelectNode; hover edge → tooltip; zoom + pan + drag.
import React, { useEffect, useMemo, useRef } from "react";
import * as d3 from "d3";

// 5 layer colors (dark+light theme adjusted).
export const LAYER_COLORS = {
  1: "#E24B4A", // Production
  2: "#f3b14a", // Engineering & Quality
  3: "#185FA5", // Logistics
  4: "#4ed084", // Warehousing & VAS
  5: "#8E44AD", // Indirect / Support
  0: "#9198b0", // Unknown
};

export const LAYER_LABELS = {
  1: "L1 · Production",
  2: "L2 · Engineering",
  3: "L3 · Logistics",
  4: "L4 · Warehousing",
  5: "L5 · Indirect",
};

export const REL_STYLE = {
  "supplies-to":     { stroke: "#9ec3ec", dasharray: null },
  "sources-from":    { stroke: "#9ec3ec", dasharray: "6 3" },
  "transports-for":  { stroke: "#f3b14a", dasharray: null },
  "stores-for":      { stroke: "#4ed084", dasharray: "2 2" },
  "services":        { stroke: "#c89adb", dasharray: "4 2" },
};

export const SEV_COLOR = { s1: "#E24B4A", s2: "#f3b14a", s3: "#4ed084", s4: "#9198b0" };

// Iter51 — edge color buckets driven by aggregated active-incident severity.
export const EDGE_STATE = {
  clear: { stroke: "#3a4150", width: 1.2, opacity: 0.55, animate: false, label: "Clear" },
  degraded: { stroke: "#f3b14a", width: 2, opacity: 0.85, animate: false, label: "Degraded" },
  critical: { stroke: "#E24B4A", width: 2.6, opacity: 0.95, animate: true, label: "Critical" },
};

// Iter58 — SLA risk-forecast tier palette for the per-node ring.
export const RISK_COLORS = {
  low: "#4ed084",      // green
  elevated: "#f3b14a", // amber
  high: "#E24B4A",     // red
};
export const RISK_LABELS = {
  low: "Low risk",
  elevated: "Elevated",
  high: "High risk",
};

export const edgeStateFor = (edge) => {
  const sev = (edge?.worst_severity || "").toLowerCase();
  if (!edge?.active_incidents) return "clear";
  if (sev === "s1" || sev === "s2") return "critical";
  return "degraded";
};

// Iter51 — Trace helpers: compute upstream / downstream / both subgraphs.
export const computeTraceSet = (nodeId, edges, direction) => {
  if (!nodeId) return { nodes: new Set(), edges: new Set() };
  const nodeOut = new Map(); // from -> [edge]
  const nodeIn = new Map();  // to -> [edge]
  for (const e of edges) {
    if (!nodeOut.has(e.from_tenant_id)) nodeOut.set(e.from_tenant_id, []);
    nodeOut.get(e.from_tenant_id).push(e);
    if (!nodeIn.has(e.to_tenant_id)) nodeIn.set(e.to_tenant_id, []);
    nodeIn.get(e.to_tenant_id).push(e);
  }
  const visitedNodes = new Set([nodeId]);
  const visitedEdges = new Set();
  const queue = [nodeId];
  while (queue.length) {
    const cur = queue.shift();
    const outgoing = nodeOut.get(cur) || [];
    const incoming = nodeIn.get(cur) || [];
    if (direction === "downstream" || direction === "both") {
      for (const e of outgoing) {
        if (visitedEdges.has(e.id)) continue;
        visitedEdges.add(e.id);
        if (!visitedNodes.has(e.to_tenant_id)) {
          visitedNodes.add(e.to_tenant_id);
          queue.push(e.to_tenant_id);
        }
      }
    }
    if (direction === "upstream" || direction === "both") {
      for (const e of incoming) {
        if (visitedEdges.has(e.id)) continue;
        visitedEdges.add(e.id);
        if (!visitedNodes.has(e.from_tenant_id)) {
          visitedNodes.add(e.from_tenant_id);
          queue.push(e.from_tenant_id);
        }
      }
    }
  }
  return { nodes: visitedNodes, edges: visitedEdges };
};

const radiusFor = (n) => 18 + Math.min(12, (n.contacts_count || 0)) + (n.active_incidents > 0 ? 4 : 0);

// Truncate part-number list label so the SVG doesn't blow up; full list lives in the tooltip.
const edgeLabel = (e) => {
  const pns = e.part_numbers || [];
  if (pns.length === 0) return e.relationship_type;
  if (pns.length === 1) return pns[0];
  return `${pns[0]} +${pns.length - 1} more`;
};

export const ChainGraph = ({ nodes, edges, layout, filters, onSelectNode, trace, slaForecast }) => {
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const simRef = useRef(null);

  // Iter51 — compute trace set when trace mode is active.
  const traceSet = useMemo(() => {
    if (!trace?.nodeId) return { nodes: new Set(), edges: new Set() };
    return computeTraceSet(trace.nodeId, edges, trace.direction || "both");
  }, [trace, edges]);

  // Iter58 — risk lookup helpers, scoped to props.
  const riskTierFor = (id) => slaForecast?.byTenant?.[id]?.risk_tier || "low";
  const riskBucketFor = (id) => slaForecast?.byTenant?.[id];
  const cascadeFromTrace = useMemo(() => {
    if (!slaForecast?.showForecast || !trace?.nodeId) return new Set();
    const c = slaForecast?.cascade?.[trace.nodeId];
    if (!c) return new Set();
    const reach = new Set();
    const dir = trace.direction || "both";
    if (dir === "downstream" || dir === "both") (c.downstream || []).forEach((d) => reach.add(d));
    if (dir === "upstream" || dir === "both") (c.upstream || []).forEach((d) => reach.add(d));
    return reach;
  }, [slaForecast, trace]);

  // Stable, filtered data — re-compute when inputs/filters change.
  const visibleData = useMemo(() => {
    let ns = nodes;
    if (filters?.layer && filters.layer !== "all") {
      ns = ns.filter((n) => String(n.layer) === String(filters.layer));
    }
    if (filters?.activeOnly) ns = ns.filter((n) => n.active_incidents > 0);
    if (filters?.typeKey && filters.typeKey !== "all") {
      ns = ns.filter((n) => n.tenant_type_key === filters.typeKey);
    }
    // Iter58 — at-risk only filter (uses forecast lookup if available).
    if (filters?.atRiskOnly && slaForecast?.byTenant) {
      ns = ns.filter((n) => {
        const tier = slaForecast.byTenant[n.id]?.risk_tier || "low";
        return tier === "elevated" || tier === "high";
      });
    }
    const allowedIds = new Set(ns.map((n) => n.id));
    const es = edges.filter((e) => allowedIds.has(e.from_tenant_id) && allowedIds.has(e.to_tenant_id));
    return { nodes: ns, edges: es };
  }, [nodes, edges, filters, slaForecast]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    if (!visibleData.nodes.length) return;

    const wrap = wrapRef.current;
    const w = wrap?.clientWidth || 1000;
    const h = wrap?.clientHeight || 640;

    svg.attr("viewBox", `0 0 ${w} ${h}`).attr("width", "100%").attr("height", "100%");

    // Arrow marker defs (one per relationship type for tinted arrows).
    const defs = svg.append("defs");
    Object.entries(REL_STYLE).forEach(([k, v]) => {
      defs.append("marker")
        .attr("id", `arrow-${k}`)
        .attr("viewBox", "0 -5 10 10")
        .attr("refX", 22)
        .attr("refY", 0)
        .attr("markerWidth", 6)
        .attr("markerHeight", 6)
        .attr("orient", "auto")
        .append("path")
          .attr("d", "M0,-5L10,0L0,5")
          .attr("fill", v.stroke);
    });

    // Iter51 — defs for the flow animation gradient on critical edges.
    defs.append("marker")
      .attr("id", "arrow-critical-flow")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 22)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
        .attr("d", "M0,-5L10,0L0,5")
        .attr("fill", EDGE_STATE.critical.stroke);
    defs.append("marker")
      .attr("id", "arrow-degraded")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 22)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
        .attr("d", "M0,-5L10,0L0,5")
        .attr("fill", EDGE_STATE.degraded.stroke);

    // Container <g> for pan/zoom
    const container = svg.append("g");
    svg.call(
      d3.zoom()
        .scaleExtent([0.4, 3])
        .on("zoom", (ev) => container.attr("transform", ev.transform))
    );

    // Pre-position nodes by layout
    const dataNodes = visibleData.nodes.map((n) => ({ ...n }));
    const dataEdges = visibleData.edges.map((e) => ({
      ...e, source: e.from_tenant_id, target: e.to_tenant_id,
    }));

    if (layout === "layered") {
      const layers = [1, 2, 3, 4, 5];
      const padTop = 60, padBottom = 50;
      const yStep = (h - padTop - padBottom) / 4;
      // Group nodes by layer
      const byLayer = {};
      dataNodes.forEach((n) => {
        const k = n.layer || 0;
        (byLayer[k] ||= []).push(n);
      });
      [...layers, 0].forEach((layer) => {
        const arr = byLayer[layer] || [];
        const y = layer === 0 ? h - 30 : padTop + (layer - 1) * yStep;
        const step = w / (arr.length + 1);
        arr.forEach((n, i) => {
          n.x = (i + 1) * step;
          n.y = y;
          n.fx = n.x; // pin vertically when layered
          n.fy = n.y;
        });
      });
    }

    // Force simulation
    const simulation = d3.forceSimulation(dataNodes)
      .force("charge", d3.forceManyBody().strength(layout === "layered" ? -200 : -380))
      .force("link", d3.forceLink(dataEdges).id((d) => d.id).distance(layout === "layered" ? 90 : 130).strength(0.6))
      .force("collide", d3.forceCollide().radius((d) => radiusFor(d) + 8));
    if (layout !== "layered") {
      simulation.force("center", d3.forceCenter(w / 2, h / 2));
    }
    simRef.current = simulation;

    // Edges
    const isTracing = Boolean(trace?.nodeId);
    const isTracedEdge = (d) => traceSet.edges.has(d.id);
    const isTracedNode = (d) => traceSet.nodes.has(d.id);

    const edgeOpacityFor = (d) => {
      const state = edgeStateFor(d);
      const base = EDGE_STATE[state].opacity;
      if (!isTracing) return base;
      return isTracedEdge(d) ? Math.max(base, 0.95) : 0.10;
    };
    const edgeStrokeFor = (d) => {
      const state = edgeStateFor(d);
      // Iter51 — if the edge has active incidents, color reflects severity.
      // Otherwise fall back to the relationship-type stroke (legacy iter49).
      if (state !== "clear") return EDGE_STATE[state].stroke;
      return (REL_STYLE[d.relationship_type] || REL_STYLE["supplies-to"]).stroke;
    };
    const edgeWidthFor = (d) => {
      const state = edgeStateFor(d);
      const w = EDGE_STATE[state].width;
      // Optional thickness scaling by active incident count (cap at +3).
      return w + Math.min(3, Math.max(0, (d.active_incidents || 0) - 1) * 0.5);
    };
    const edgeMarkerFor = (d) => {
      const state = edgeStateFor(d);
      if (state === "critical") return "url(#arrow-critical-flow)";
      if (state === "degraded") return "url(#arrow-degraded)";
      return `url(#arrow-${d.relationship_type || "supplies-to"})`;
    };

    const edgeG = container.append("g").attr("data-testid", "chain-edges");
    const edgeSel = edgeG.selectAll("line").data(dataEdges, (d) => d.id).enter()
      .append("line")
      .attr("stroke", edgeStrokeFor)
      .attr("stroke-opacity", edgeOpacityFor)
      .attr("stroke-width", edgeWidthFor)
      .attr("stroke-dasharray", (d) => {
        const state = edgeStateFor(d);
        if (state !== "clear") return null; // solid for active edges
        return (REL_STYLE[d.relationship_type] || REL_STYLE["supplies-to"]).dasharray || null;
      })
      .attr("marker-end", edgeMarkerFor)
      .attr("data-edge-state", (d) => edgeStateFor(d))
      .attr("data-traced", (d) => isTracing && isTracedEdge(d) ? "true" : "false")
      .attr("data-testid", (d) => `chain-edge-${d.id}`);

    // Iter51 — flow animation on critical / traced edges (animate stroke-dashoffset).
    edgeSel
      .filter((d) => {
        const state = edgeStateFor(d);
        return state === "critical" || (isTracing && isTracedEdge(d));
      })
      .attr("stroke-dasharray", "6 4")
      .append("animate")
        .attr("attributeName", "stroke-dashoffset")
        .attr("from", "0")
        .attr("to", "-20")
        .attr("dur", "1.0s")
        .attr("repeatCount", "indefinite");

    // Edge label group (hidden until hover for cleanliness)
    const labelG = container.append("g").style("pointer-events", "none");
    const labelSel = labelG.selectAll("text").data(dataEdges).enter()
      .append("text")
      .text(edgeLabel)
      .attr("fill", "var(--text-dim, #c5cad8)")
      .attr("font-family", "DM Mono, monospace")
      .attr("font-size", 9)
      .attr("opacity", 0.75)
      .attr("text-anchor", "middle");

    // Tooltips via <title> for simple browser-native hover
    edgeSel.append("title").text((d) => {
      const lines = [
        `${d.relationship_type}`,
        d.part_numbers?.length ? `Parts: ${d.part_numbers.join(", ")}` : null,
        d.from_contacts?.length ? `From contacts: ${d.from_contacts.map((c) => c.name).join(", ")}` : null,
        d.to_contacts?.length ? `To contacts: ${d.to_contacts.map((c) => c.name).join(", ")}` : null,
        d.notes || null,
      ].filter(Boolean);
      return lines.join("\n");
    });

    // Node group
    const nodeG = container.append("g").attr("data-testid", "chain-nodes");
    const nodeSel = nodeG.selectAll("g").data(dataNodes, (d) => d.id).enter()
      .append("g")
      .attr("data-testid", (d) => `chain-node-${d.id}`)
      .attr("data-tenant", (d) => d.name)
      .attr("data-traced", (d) => isTracing && isTracedNode(d) ? "true" : "false")
      .style("opacity", (d) => isTracing && !isTracedNode(d) ? 0.18 : 1)
      .style("cursor", "pointer")
      .on("click", (_ev, d) => onSelectNode?.(d))
      .call(
        d3.drag()
          .on("start", (ev, d) => {
            if (!ev.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on("drag", (ev, d) => {
            d.fx = ev.x;
            d.fy = ev.y;
          })
          .on("end", (ev, d) => {
            if (!ev.active) simulation.alphaTarget(0);
            if (layout !== "layered") { d.fx = null; d.fy = null; }
          })
      );

    // Iter58 — SLA risk forecast ring (drawn under the main circle so the
    // node fill sits on top). Visible only when slaForecast.showForecast.
    if (slaForecast?.showForecast) {
      nodeSel.filter((d) => ["elevated", "high"].includes(riskTierFor(d.id)))
        .append("circle")
        .attr("class", "sla-risk-ring")
        .attr("r", (d) => radiusFor(d) + 3)
        .attr("fill", "none")
        .attr("stroke", (d) => RISK_COLORS[riskTierFor(d.id)])
        .attr("stroke-width", 3)
        .attr("opacity", 0.85)
        .attr("data-risk-tier", (d) => riskTierFor(d.id))
        .attr("data-testid", (d) => `chain-risk-ring-${d.id}`);

      // Cascade halo on downstream/upstream nodes when the traced node is at risk.
      if (cascadeFromTrace.size > 0) {
        nodeSel.filter((d) => cascadeFromTrace.has(d.id))
          .append("circle")
          .attr("class", "sla-cascade-halo")
          .attr("r", (d) => radiusFor(d) + 10)
          .attr("fill", "none")
          .attr("stroke", "#E24B4A")
          .attr("stroke-width", 1.4)
          .attr("stroke-dasharray", "3 3")
          .attr("opacity", 0.65)
          .attr("data-testid", (d) => `chain-cascade-halo-${d.id}`)
          .append("animate")
            .attr("attributeName", "stroke-dashoffset")
            .attr("from", 0)
            .attr("to", 12)
            .attr("dur", "1.2s")
            .attr("repeatCount", "indefinite");
      }
    }

    // Pulsing ring for active S1/S2
    nodeSel.filter((d) => d.active_incidents > 0 && (d.worst_severity === "s1" || d.worst_severity === "s2"))
      .append("circle")
      .attr("r", (d) => radiusFor(d) + 6)
      .attr("fill", "none")
      .attr("stroke", (d) => SEV_COLOR[d.worst_severity] || "#E24B4A")
      .attr("stroke-width", 1.5)
      .attr("opacity", 0.6)
      .append("animate")
        .attr("attributeName", "r")
        .attr("from", (d) => radiusFor(d) + 6)
        .attr("to", (d) => radiusFor(d) + 16)
        .attr("dur", "1.4s")
        .attr("repeatCount", "indefinite");

    // Main circle
    nodeSel.append("circle")
      .attr("r", (d) => radiusFor(d))
      .attr("fill", (d) => LAYER_COLORS[d.layer || 0])
      .attr("fill-opacity", 0.92)
      .attr("stroke", "rgba(0,0,0,0.45)")
      .attr("stroke-width", 1.4);

    // Initials
    nodeSel.append("text")
      .text((d) => d.initials)
      .attr("text-anchor", "middle")
      .attr("dy", 4)
      .attr("fill", "#0d0f18")
      .attr("font-family", "Syne, sans-serif")
      .attr("font-weight", 700)
      .attr("font-size", 11)
      .attr("pointer-events", "none");

    // Tenant name label
    nodeSel.append("text")
      .text((d) => d.name)
      .attr("text-anchor", "middle")
      .attr("dy", (d) => radiusFor(d) + 14)
      .attr("fill", "var(--text, #f0f2f8)")
      .attr("font-family", "DM Mono, monospace")
      .attr("font-size", 10)
      .attr("pointer-events", "none");

    // Active incident chip
    nodeSel.filter((d) => d.active_incidents > 0)
      .append("text")
      .text((d) => `${d.active_incidents}`)
      .attr("text-anchor", "middle")
      .attr("dy", (d) => -(radiusFor(d) + 4))
      .attr("fill", (d) => SEV_COLOR[d.worst_severity] || "#E24B4A")
      .attr("font-family", "DM Mono, monospace")
      .attr("font-weight", 700)
      .attr("font-size", 11);

    // Title tooltip on node
    nodeSel.append("title").text((d) => {
      const lines = [
        d.name,
        d.tenant_type_key ? `${d.tenant_type_key} · L${d.layer || "?"}` : "(no type)",
        d.active_incidents ? `${d.active_incidents} active incident(s) · worst ${d.worst_severity?.toUpperCase()}` : null,
        d.contacts_count ? `${d.contacts_count} contact(s)` : null,
      ];
      // Iter58 — risk forecast addendum.
      if (slaForecast?.showForecast) {
        const bucket = riskBucketFor(d.id);
        const tier = bucket?.risk_tier || "low";
        if (tier !== "low") {
          const forecasts = bucket?.forecast_breaches || [];
          const alreadyBreached = forecasts.filter((b) => b.already_breached).length;
          const upcoming = forecasts.length - alreadyBreached;
          const parts = [];
          if (upcoming) parts.push(`${upcoming} forecast to breach in next 24h`);
          if (alreadyBreached) parts.push(`${alreadyBreached} already breaching`);
          lines.push(`SLA risk: ${RISK_LABELS[tier]} — ${parts.join(", ") || `${forecasts.length} incident(s)`}`);
          forecasts.slice(0, 3).forEach((b) => {
            const m = b.minutes_to_deadline;
            const when = m <= 0
              ? `breached ${Math.abs(m)}m ago`
              : `${Math.floor(m / 60)}h ${m % 60}m`;
            lines.push(`  · ${b.incident_id} ${b.severity.toUpperCase()} — ${when}`);
          });
        }
      }
      return lines.filter(Boolean).join("\n");
    });

    simulation.on("tick", () => {
      edgeSel
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x)
        .attr("y2", (d) => d.target.y);
      labelSel
        .attr("x", (d) => (d.source.x + d.target.x) / 2)
        .attr("y", (d) => (d.source.y + d.target.y) / 2);
      nodeSel.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    return () => simulation.stop();
  }, [visibleData, layout, onSelectNode, trace, traceSet, slaForecast, cascadeFromTrace]);

  return (
    <div ref={wrapRef} className="w-full h-full" data-testid="chain-graph-canvas" style={{ minHeight: 500 }}>
      <svg ref={svgRef} role="img" aria-label="Supply-chain relationship graph" />
    </div>
  );
};
